import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {type ActionItem, type FullTextButton} from 'ui-library/headers/Header';
import * as DateUtil from 'util/DateUtil';
import {assertNever} from 'util/Types';

import {StudyDesignTabEditState} from '../design-tabs/StudyDesignTabSavingButtons';
import {type RunDesignActions} from './StudyOverviewContexts';

export interface StudyHeaderFields {
  id: string;
  name: string;
  editUrl: string;
  studySchema: {studyType: StudyType};
}

const PRIMARY = 'primary';
const SECONDARY = 'secondary';
const SAVING_ICON = 'spinner-third';

export function getProcedureRunHeaderButtons(actions: RunDesignActions): Array<FullTextButton> {
  switch (actions.editState) {
    case StudyDesignTabEditState.SAVED:
      return [
        {
          label: 'Edit design',
          variant: PRIMARY,
          icon: 'edit',
          onClick: actions.onEdit,
          disabled: actions.isEditDisabled,
          disabledTooltip: actions.editDisabledTooltip,
          dataTestAttrs: {component: 'ProcedureRunHeader', element: 'editDesignButton'},
        },
      ];
    case StudyDesignTabEditState.EDITING:
      // Header renders right-to-left, so the primary button comes first.
      return [
        {
          label: 'Update design',
          variant: PRIMARY,
          onClick: actions.onSave,
          disabled: actions.isSaveDisabled,
          disabledTooltip: actions.saveDisabledTooltip,
          dataTestAttrs: {component: 'ProcedureRunHeader', element: 'updateDesignButton'},
        },
        {label: 'Cancel', variant: SECONDARY, onClick: actions.onCancel},
      ];
    case StudyDesignTabEditState.SAVING:
      return [{label: 'Saving', variant: PRIMARY, icon: SAVING_ICON, onClick: noop, disabled: true}];
    default:
      return assertNever(actions.editState);
  }
}

export interface ProcedureRunPlanHeaderButtonParams {
  hasUnsavedPlanChanges: boolean;
  isSavingPlan: boolean;
  /** Whether condition validation blocks saving the draft. */
  isSaveDisabled: boolean;
  saveDisabledTooltip?: string;
  hasPlanErrors: boolean;
  shouldDisableCreate: boolean;
  onSavePlan: () => void;
  onCreateRun: () => void;
}

export function getProcedureRunPlanHeaderButtons({
  hasUnsavedPlanChanges,
  isSavingPlan,
  isSaveDisabled,
  saveDisabledTooltip,
  hasPlanErrors,
  shouldDisableCreate,
  onSavePlan,
  onCreateRun,
}: ProcedureRunPlanHeaderButtonParams): Array<FullTextButton> {
  const savePlan: FullTextButton = isSavingPlan
    ? {label: 'Saving', variant: SECONDARY, icon: SAVING_ICON, onClick: noop, disabled: true}
    : {
        label: 'Save plan',
        variant: SECONDARY,
        onClick: onSavePlan,
        disabled: !hasUnsavedPlanChanges || isSaveDisabled,
        disabledTooltip: isSaveDisabled
          ? saveDisabledTooltip
          : !hasUnsavedPlanChanges
            ? 'No changes to save'
            : undefined,
        dataTestAttrs: {component: 'ProcedureRunPlanHeader', element: 'savePlanButton'},
      };
  const createRun: FullTextButton = {
    label: hasUnsavedPlanChanges ? 'Save and create run' : 'Create run',
    variant: PRIMARY,
    onClick: onCreateRun,
    disabled: isSavingPlan || hasPlanErrors || shouldDisableCreate,
    dataTestAttrs: {component: 'ProcedureRunPlanHeader', element: 'createRunButton'},
  };
  // Header renders right-to-left, so the primary button comes first.
  return [createRun, savePlan];
}

export function actionToFullTextButton(action: ActionItem, variant: 'primary' | 'secondary'): FullTextButton {
  return {
    label: action.name,
    variant,
    icon: action.icon,
    onClick: () => action.onSelect?.(),
    disabled: action.isDisabled,
    disabledTooltip: action.disabledTooltip,
  };
}

export function CreatedAt({createdAt}: {createdAt?: string}): JSX.Element | null {
  const {classes} = useStyles();
  if (createdAt == null) {
    return null;
  }
  return (
    <div className={classes.createdAt}>Created at {DateUtil.readableTime(createdAt, {showTime: true})}</div>
  );
}

const useStyles = createStyles((theme) => ({
  createdAt: {
    color: theme.colors.gray[6],
    whiteSpace: 'nowrap',
    padding: theme.spacing.sm,
  },
}));
